import type { ConditionEnv } from '@seen/core/observation-logic/evaluate'
import {
  type InstrumentAnswers,
  instrumentAnswerKey,
} from '@seen/core/observation-logic/instruments/records'
import {
  instrumentAnswerOptions,
  instrumentItems,
} from '@seen/core/terminology/instruments/registry'
import type {
  InstrumentItem,
  InstrumentTemplate,
} from '@seen/core/terminology/instruments/types'
import { format, parseISO } from 'date-fns'
import type { LastAnswer } from '#src/components/rich-text-editor/ObservationNode/panel/ChoiceMatrix.tsx'
import type { PriorAnswer } from '#src/components/rich-text-editor/ObservationNode/panel/useInstrumentCapture.ts'

// What an instrument's conditions read: the context variables, and its own
// items' answers by code.
export function instrumentEnv(
  template: InstrumentTemplate,
  answers: InstrumentAnswers,
  variable: ConditionEnv['variable']
): ConditionEnv {
  const items = instrumentItems(template)
  return {
    variable,
    observation: (operand) => {
      const item = items.find(
        (candidate) =>
          candidate.coding.code === operand.code &&
          (!operand.system || candidate.coding.system === operand.system)
      )
      return item
        ? (answers[instrumentAnswerKey(item.coding)] ?? null)
        : 'missing'
    },
  }
}

// Every question of an instrument, in a grid or on its own, puts its label
// in this column and its answers after it, so the answers of every row
// start at one edge. A percentage track resolves against the grid's own
// width, so separate grids of the same width share the edge.
export const LABEL_COLUMN = 'max(14rem, 40%)'

// The label's first line sits on the answers' text line.
export const LABEL_CELL = 'self-stretch pt-1.5 pr-3 leading-5'

// A run of items sharing one answer list, for one grid; other items stand
// alone.
export type ItemRun =
  | { kind: 'matrix'; items: InstrumentItem[] }
  | { kind: 'single'; item: InstrumentItem }

export function itemRuns(items: readonly InstrumentItem[]): ItemRun[] {
  const runs: ItemRun[] = []
  for (const item of items) {
    const options = instrumentAnswerOptions(item)
    const coded = item.scaleType === 'Ord' || item.scaleType === 'Nom'
    const last = runs[runs.length - 1]
    const key = coded && options.length ? answerListKey(item) : null
    if (
      key &&
      last?.kind === 'matrix' &&
      answerListKey(last.items[0] as InstrumentItem) === key
    ) {
      last.items.push(item)
    } else if (key) {
      runs.push({ kind: 'matrix', items: [item] })
    } else {
      runs.push({ kind: 'single', item })
    }
  }
  // A grid of one is just a question.
  return runs.map((run) =>
    run.kind === 'matrix' && run.items.length === 1
      ? { kind: 'single', item: run.items[0] as InstrumentItem }
      : run
  )
}

function answerListKey(item: InstrumentItem): string {
  return (
    item.answerSet ??
    instrumentAnswerOptions(item)
      .map((option) => `${option.system ?? ''}|${option.code}`)
      .join(',')
  )
}

// A score band's colour as the panel badge and the facesheet show it.
export function bandColourClass(severity: string | undefined): string {
  switch (severity) {
    case 'red':
      return 'bg-red-50 text-red-700'
    case 'orange':
      return 'bg-orange-50 text-orange-800'
    case 'yellow':
      return 'bg-yellow-50 text-yellow-800'
    default:
      return 'bg-green-50 text-green-800'
  }
}

// Earlier answers from other days, marked on their cells, by answer key.
// One from this visit (an instrument captured in this note) is carried over
// instead.
export function lastAnswers(
  items: readonly InstrumentItem[],
  priors: Readonly<Record<string, PriorAnswer>>,
  notePanels: ReadonlyMap<string, string>,
  asOf: string
) {
  const last: Record<string, LastAnswer> = {}
  for (const item of items) {
    const key = instrumentAnswerKey(item.coding)
    const prior = priors[key]
    const code =
      prior?.value.kind === 'coding' ? prior.value.value[0]?.code : undefined
    const option = instrumentAnswerOptions(item).find(
      (candidate) => candidate.code === code
    )
    if (!prior || !option || (prior.groupId && notePanels.has(prior.groupId))) {
      continue
    }
    const day = prior.date
      ? prior.date === asOf
        ? 'today'
        : format(parseISO(prior.date), 'MMM d')
      : null
    last[key] = {
      option,
      day,
      source: prior.date ? `recorded ${prior.date}` : 'recorded earlier',
    }
  }
  return last
}

export interface LastValue {
  value: number | string
  // When it was recorded, as shown: "Mar 2", "today".
  day: string | null
}

// Earlier number and text answers from other days, by answer key, offered on
// their rows while unanswered. One from this visit is carried over instead.
export function lastValues(
  items: readonly InstrumentItem[],
  priors: Readonly<Record<string, PriorAnswer>>,
  notePanels: ReadonlyMap<string, string>,
  asOf: string
) {
  const last: Record<string, LastValue> = {}
  for (const item of items) {
    const key = instrumentAnswerKey(item.coding)
    const prior = priors[key]
    if (
      !prior ||
      (prior.value.kind !== 'number' && prior.value.kind !== 'string') ||
      (prior.groupId && notePanels.has(prior.groupId))
    ) {
      continue
    }
    last[key] = {
      value: prior.value.value,
      day: prior.date
        ? prior.date === asOf
          ? 'today'
          : format(parseISO(prior.date), 'MMM d')
        : null,
    }
  }
  return last
}
